import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Breadcrumb,
  BreadcrumbItem,
  Input,
} from "reactstrap";
import LeaveTypeTable from "./LeaveTypeTable";
import LeaveTypeFormModal from "./LeaveTypeFormModal";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import CommonDropDown from "../common/CommonDropDown";
import { useNavigate, Link } from "react-router-dom";
import Breadcrumbs from "../../common-elements/breadcrumbs/Breadcrumbs";
import SearchBar from "../../common-elements/search-bar/SearchBar";
import { useTranslation } from "react-i18next";

export interface LeaveType {
  id: number;
  typeName: string;
  description: string;
  typicalUsage: string;
}

const initialData: LeaveType[] = [
  {
    id: 1,
    typeName: "Parental",
    description: "Time off surrounding childbirth/adoption (includes maternity/paternity).",
    typicalUsage: "newborn, childcare, maternity, paternity, bonding",
  },
  {
    id: 2,
    typeName: "Annual",
    description: "Paid time off accrued annually or unlimited.",
    typicalUsage: "vacation, rest, relaxation, personal, planned",
  },
  {
    id: 3,
    typeName: "Sick",
    description: "Paid leave to recover from illness.",
    typicalUsage: "illness, doctor, medical, health, recovery, family-care",
  },
  {
    id: 4,
    typeName: "Casual",
    description: "For short, unexpected absences, often for personal matters or unforeseen circumstances.",
    typicalUsage: "personal, casual, emergency, unexpected, personal-business",
  },
  {
    id: 5,
    typeName: "Bereavement",
    description: "Time off to attend to matters related to the death of a loved one.",
    typicalUsage: "funeral, grief, memorial, family, loss",
  },
  {
    id: 6,
    typeName: "Unpaid",
    description: "Unpaid leave for personal reasons not covered by other leave types.",
    typicalUsage: "personal, extended, travel, sabbatical, unpaid",
  },
];

const LeaveTypeList: React.FC = () => {
  const [LeaveTypes, setLeaveTypes] = useState<LeaveType[]>(initialData);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState<LeaveType | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState("");
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleAdd = () => {
    setEditData(null);
    setModalOpen(true);
  };

  const handleEdit = (data: LeaveType) => {
    setEditData(data);
    setModalOpen(true);
  };

  const handleDelete = (id: number) => {
    setDeleteId(id);
  };

  const handleModalSubmit = (data: Omit<LeaveType, "id">, id?: number) => {
    if (id) {
      setLeaveTypes((prev) =>
        prev.map((item) => (item.id === id ? { ...item, ...data } : item))
      );
    } else {
      setLeaveTypes((prev) => [
        ...prev,
        {
          id: prev.length ? Math.max(...prev.map((h) => h.id)) + 1 : 1,
          ...data,
        },
      ]);
    }
    setModalOpen(false);
    setEditData(null);
  };

  const confirmDelete = () => {
    if (deleteId !== null) {
      setLeaveTypes((prev) => prev.filter((item) => item.id !== deleteId));
      setDeleteId(null);
    }
  };

  // Filter leave types by search text (typeName, description, or typicalUsage)
  const filteredLeaveTypes = LeaveTypes.filter(
    (item) =>
      item.typeName.toLowerCase().includes(searchText.toLowerCase()) ||
      item.description.toLowerCase().includes(searchText.toLowerCase()) ||
      item.typicalUsage.toLowerCase().includes(searchText.toLowerCase())
  );

  useEffect(() => {
    if (modalOpen) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => document.body.classList.remove("modal-open");
  }, [modalOpen]);

  return (
    <>
      <Breadcrumbs
        mainTitle={t("LeaveType")}
        parent={t("Master Data")}
        parentLink={`${process.env.PUBLIC_URL}/masterdata/leave-type`}
      />
      <Container fluid>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <div className="list-product-header">
                  <div className="header-container">
                    {/* Search bar (left) */}
                    <SearchBar
                      label={t("Search")}
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      placeholder={t("SearchByLeaveName")}
                    />
                    {/* Buttons (right) */}
                    <div className="actions-container">
                      <Link
                        className="btn btn-success"
                        to="/masterdata/import-leave-type"
                      >
                        <i className="fa fa-download" /> {t("Import")}
                      </Link>
                      <CommonDropDown color="info" />
                      <div className="btn btn-primary" onClick={handleAdd}>
                        <i className="fa fa-plus" style={{ marginRight: 4 }} />{" "}
                        {t("AddLeaveType")}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="list-product">
                  <div className="table-responsive">
                    <LeaveTypeTable
                      data={filteredLeaveTypes}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <LeaveTypeFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleModalSubmit}
        editData={editData}
      />
      <ConfirmDeleteModal
        open={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={confirmDelete}
        name={LeaveTypes.find((item) => item.id === deleteId)?.typeName}
      />
    </>
  );
};

export default LeaveTypeList;